import { useSession } from '../../app/session.js';
import { useViewOrFallback } from '../../app/view.js';
import { ChoiceSelect } from '../../components/Choice.js';
import { Icon } from '../../components/Icon.js';
import { useConfirm, useModal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Pill } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { telLink } from '../../lib/device.js';
import { printHtml } from '../../lib/export.js';
import { fv } from '../../lib/form.js';
import { fmtDay, fmtDT, num, rp, today } from '../../lib/format.js';
import { label } from '../../lib/status.js';
import type { Nota } from '../nota/types.js';
import { loadingDocHtml, tagihanDocHtml } from './docs.js';
import { type Billing, type RouteDetail, type RouteRow, routeStat } from './types.js';

// Rute Kirim — port newRouteModal / openRoute / addToRoute / closeRouteModal v1 (ops.js).

const checkedIds = (f: HTMLFormElement) =>
  [...f.querySelectorAll<HTMLInputElement>('[name=nid]')].filter((c) => c.checked).map((c) => Number(c.value));

/** Daftar nota pilihan (Buat Rute / Tambah nota) — v1 `<label class="line uc">…`. */
function NotaPick({ notas }: { notas: Nota[] }) {
  return (
    <div className="lines">
      {notas.map((n) => (
        <label className="line uc" key={n.id}>
          <input type="checkbox" className="check-in" name="nid" value={n.id} />
          <div className="body">
            <b>{n.store_name}</b>
            <span>{`${n.area || '-'} · ${n.no} · ${label('pm', n.pay_method)}`}</span>
          </div>
          <em className="price">{rp(n.total)}</em>
        </label>
      ))}
    </div>
  );
}

export function useRouteOps() {
  const { open } = useModal();
  const confirm = useConfirm();
  const toast = useToast();
  const { meta, refreshCounts, can } = useSession();
  const kelola = can('rute.kelola');
  // Juga dibuka dari luar halaman (palet pencarian) — lihat useViewOrFallback().
  const { go, reload: render } = useViewOrFallback();
  const goRute = () => go('/rute');

  async function newRouteModal(): Promise<void> {
    const drivers = meta.drivers ?? [];
    if (!drivers.length) return toast('Belum ada driver aktif — tambahkan di menu Pengguna.', true);
    const { items: notas } = await api<{ items: Nota[] }>('GET', '/api/notas?status=disiapkan&unrouted=1');
    if (!notas.length) return toast('Tidak ada nota "Perlu dimuat" yang belum masuk rute.', true);
    open({
      wide: true,
      title: 'Buat Rute Kirim',
      body: (
        <>
          <div className="grid2">
            <label className="f">
              Driver
              <select className="inp" name="driver_id" required>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="f">
              Tanggal kirim
              <input autoComplete="off" className="inp" type="date" name="ship_date" defaultValue={today()} />
            </label>
          </div>
          <ChoiceSelect kind="area" name="area" empty="— tanpa area —" label="Area / rute (opsional)" />
          <div className="row between">
            <b>
              {'Pilih nota '}
              <span className="muted small">(urutan daftar = urutan antar)</span>
            </b>
            <label className="check">
              <input
                type="checkbox"
                id="rtAll"
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  for (const c of e.currentTarget.form?.querySelectorAll<HTMLInputElement>('[name=nid]') ?? []) c.checked = on;
                }}
              />
              {' Pilih semua'}
            </label>
          </div>
          <NotaPick notas={notas} />
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            Buat Rute
          </button>
        </>
      ),
      submit: async (f, m) => {
        const nota_ids = checkedIds(f);
        if (!nota_ids.length) throw new Error('Pilih minimal satu nota.');
        const v = (k: string) => fv(f, k);
        const res = await api<{ id: number; no: string }>('POST', '/api/routes', {
          driver_id: Number(v('driver_id')),
          ship_date: v('ship_date'),
          area: v('area'),
          nota_ids,
        });
        m.close();
        toast(`Rute ${res.no} dibuat.`);
        render();
        await openRoute(res.id);
      },
    });
  }

  async function openRoute(id: number): Promise<void> {
    const d = await api<RouteDetail>('GET', `/api/routes/${id}`);
    const r = d.item;
    const L = d.loading;
    const [slbl, scls] = routeStat(r);
    const closed = ['selesai', 'batal'].includes(r.status);
    const canEdit = kelola && !closed && r.siap > 0;
    const short = L.products.some((p) => p.short);
    const B = d.billing;
    const bt = B.totals;
    const hasOpen = r.siap > 0 || r.jalan > 0;
    const canClose = kelola && r.nota_count > 0 && !hasOpen && r.status !== 'selesai' && r.status !== 'batal';
    const anyDelivered = B.items.some((x) => x.delivered || x.retur);

    open({
      wide: true,
      title: (
        <>
          Rute <span className="mono">{r.no}</span> <span className={`pill ${scls}`}>{slbl}</span>
        </>
      ),
      body: (mm) => {
        const moveStop = (nid: number, dir: number) =>
          mm.run(async () => {
            const ids = d.notas.map((x) => x.id);
            const i = ids.indexOf(nid);
            const j = i + dir;
            if (i < 0 || j < 0 || j >= ids.length) return;
            [ids[i], ids[j]] = [ids[j] as number, ids[i] as number];
            await api('PUT', `/api/routes/${id}`, { order: ids });
            mm.close();
            await openRoute(id);
          });
        const rm = async (nid: number) => {
          const n = d.notas.find((x) => x.id === nid);
          if (!n) return;
          if (
            await confirm(
              'Keluarkan dari rute',
              `Nota ${n.no} (${n.store_name}) dikeluarkan dari rute dan kembali ke "Perlu dimuat".`,
              { danger: true, ok: 'Keluarkan' },
            )
          )
            await mm.run(async () => {
              await api('PUT', `/api/routes/${id}`, { remove_nota_ids: [n.id] });
              mm.close();
              toast('Nota dikeluarkan.');
              await openRoute(id);
              void refreshCounts();
            });
        };
        return (
          <>
            <div className="grid-2">
              <dl className="kv">
                <dt>Driver</dt>
                <dd>
                  {`${r.driver_name}${r.driver_phone ? ' · ' : ''}`}
                  {r.driver_phone ? <a href={telLink(r.driver_phone)}>{r.driver_phone}</a> : null}
                </dd>
                <dt>Tgl kirim</dt>
                <dd>{fmtDay(r.ship_date)}</dd>
                <dt>Area</dt>
                <dd>{r.area || '—'}</dd>
              </dl>
              <dl className="kv">
                <dt>Jumlah toko</dt>
                <dd>{num(r.nota_count)}</dd>
                <dt>Total tagihan</dt>
                <dd className="b">{rp(r.total)}</dd>
                <dt>Dibuat</dt>
                <dd>{`${r.created_by_name || ''} · ${fmtDT(r.created_at)}`}</dd>
              </dl>
            </div>
            {short ? (
              <div className="note danger">
                Stok kurang untuk sebagian barang — barang baru keluar stok saat verifikasi muat per nota.
              </div>
            ) : null}
            <div className="sec-h">
              <h3>Rekap Loading (total barang dimuat)</h3>
              <span className="small muted">urutan muat = kebalikan urutan antar</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Produk</th>
                    <th className="right">Total muat</th>
                    <th className="right">Stok</th>
                  </tr>
                </thead>
                <tbody>
                  {L.products.length ? (
                    L.products.map((p) => (
                      <tr key={p.key}>
                        <td>{p.name}</td>
                        <td className="right b">{`${num(p.total)} ${p.unit}`}</td>
                        <td
                          className={`right ${p.short ? 'danger-txt b' : 'muted'}`}
                        >{`${num(p.stock)} ${p.base_unit || ''}`}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="muted">
                        Belum ada barang.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th className="right">Grand total</th>
                    <th className="right">{num(L.grand)}</th>
                    <th />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="sec-h">
              <h3>{`Urutan antar (${num(r.nota_count)} toko)`}</h3>
            </div>
            <div className="lines">
              {d.notas.length ? (
                d.notas.map((n, i) => (
                  <div className="line" key={n.id}>
                    <span className="rt-seq">{i + 1}</span>
                    <div className="body">
                      <b>{n.store_name}</b>
                      <span>
                        {`${n.no} · ${label('pm', n.pay_method)} · `}
                        <Pill kind="nota" v={n.status} />
                      </span>
                    </div>
                    <em className="price">{rp(n.total)}</em>
                    {canEdit && n.status === 'disiapkan' ? (
                      <span className="rt-ops">
                        <button
                          type="button"
                          className="icon-btn"
                          data-m="up"
                          data-id={n.id}
                          aria-label="Naik"
                          onClick={() => void moveStop(n.id, -1)}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          data-m="down"
                          data-id={n.id}
                          aria-label="Turun"
                          onClick={() => void moveStop(n.id, 1)}
                        >
                          ▼
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          data-m="rm"
                          data-id={n.id}
                          aria-label="Keluarkan"
                          onClick={() => void rm(n.id)}
                        >
                          <Icon n="x" cls="i-sm" />
                        </button>
                      </span>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="empty">Belum ada nota di rute ini.</div>
              )}
            </div>
            {anyDelivered ? (
              <>
                <div className="sec-h">
                  <h3>Surat Jalan Tagihan</h3>
                  <span className="small muted">cocokkan dengan uang driver</span>
                </div>
                <div className="grid3">
                  <div className="kpi">
                    <span className="lbl">Tagihan cash</span>
                    <span className="val">{rp(bt.tagihan)}</span>
                  </div>
                  <div className="kpi">
                    <span className="lbl">Terkumpul (driver)</span>
                    <span className="val ok-txt">{rp(bt.realisasi)}</span>
                  </div>
                  <div className="kpi">
                    <span className="lbl">Sisa piutang</span>
                    <span className={`val ${bt.sisa > 0 ? 'danger-txt' : ''}`}>{rp(bt.sisa)}</span>
                  </div>
                </div>
                {B.deposit ? (
                  <div className={`note ${B.deposit.diff ? 'danger' : 'info'}`}>
                    {'Setoran '}
                    <b>{B.deposit.no}</b>
                    {` · tercatat ${rp(B.deposit.expected)}, diterima ${rp(B.deposit.received)}${B.deposit.diff ? ` · selisih ${rp(B.deposit.diff)}` : ' · cocok'}${B.deposit.note ? ` — ${B.deposit.note}` : ''}`}
                  </div>
                ) : B.cash_unsettled > 0 ? (
                  <div className="note">
                    {'Cash belum disetor: '}
                    <b>{rp(B.cash_unsettled)}</b>
                    {`.${canClose ? ' Tekan "Tutup Rute & Setor" untuk mencocokkan.' : ''}`}
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        );
      },
      foot: (mm) => (
        <>
          <button type="button" className="btn" data-m="print" onClick={() => printHtml(loadingDocHtml(meta.settings, r, L))}>
            <Icon n="print" cls="i-sm" /> Rekap Loading
          </button>
          {anyDelivered ? (
            <button
              type="button"
              className="btn"
              data-m="printBill"
              onClick={() => printHtml(tagihanDocHtml(meta.settings, r, B))}
            >
              <Icon n="print" cls="i-sm" /> Surat Jalan Tagihan
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="btn btn-soft"
              data-m="add"
              onClick={async () => {
                mm.close();
                await addToRoute(id);
              }}
            >
              Tambah nota
            </button>
          ) : null}
          {canClose ? (
            // v1 memakai data-m="close" di sini (bentrok dengan tombol tutup modal); di v2 aksinya dijalankan.
            <button
              type="button"
              className="btn btn-primary"
              data-m="close"
              onClick={() => {
                mm.close();
                closeRouteModal(r, B);
              }}
            >
              <Icon n="wallet" cls="i-sm" /> Tutup Rute &amp; Setor
            </button>
          ) : null}
          {kelola && !closed && r.jalan === 0 && r.selesai === 0 ? (
            <button
              type="button"
              className="btn btn-danger"
              data-m="cancel"
              onClick={async () => {
                if (
                  await confirm('Batalkan rute', 'Semua nota kembali ke "Perlu dimuat". Rute dibatalkan.', {
                    danger: true,
                    ok: 'Batalkan rute',
                  })
                )
                  await mm.run(async () => {
                    await api('POST', `/api/routes/${id}/cancel`);
                    mm.close();
                    toast('Rute dibatalkan.');
                    goRute();
                  });
              }}
            >
              Batalkan rute
            </button>
          ) : null}
        </>
      ),
    });
  }

  async function addToRoute(id: number): Promise<void> {
    const { items: notas } = await api<{ items: Nota[] }>('GET', '/api/notas?status=disiapkan&unrouted=1');
    if (!notas.length) {
      toast('Tidak ada nota lain yang bisa ditambahkan.', true);
      return openRoute(id);
    }
    open({
      wide: true,
      title: 'Tambah nota ke rute',
      body: <NotaPick notas={notas} />,
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            Tambah
          </button>
        </>
      ),
      submit: async (f, m) => {
        const add_nota_ids = checkedIds(f);
        if (!add_nota_ids.length) throw new Error('Pilih minimal satu nota.');
        await api('PUT', `/api/routes/${id}`, { add_nota_ids });
        m.close();
        toast('Nota ditambahkan.');
        await openRoute(id);
        void refreshCounts();
      },
    });
  }

  /** Tutup rute: cocokkan uang cash driver dengan Surat Jalan Tagihan → setoran + rute selesai. */
  function closeRouteModal(r: RouteRow, B: Billing) {
    const expected = B.cash_unsettled;
    open({
      title: (
        <>
          Tutup rute <span className="mono">{r.no}</span>
        </>
      ),
      body: (
        <>
          <div className="note info">
            Semua nota sudah diproses. Cocokkan uang cash yang dibawa driver dengan Surat Jalan Tagihan.
          </div>
          <dl className="kv">
            <dt>Driver</dt>
            <dd>{r.driver_name}</dd>
            <dt>Tagihan cash</dt>
            <dd>{rp(B.totals.tagihan)}</dd>
            <dt>Cash belum disetor</dt>
            <dd className="b">{rp(expected)}</dd>
          </dl>
          {expected > 0 ? (
            <>
              <label className="f">
                Uang diterima dari driver
                <input autoComplete="off" className="inp amt" type="number" name="received" min="0" defaultValue={expected} />
              </label>
              <label className="f">
                Keterangan (wajib bila ada selisih)
                <input autoComplete="off" className="inp" name="note" maxLength={500} />
              </label>
            </>
          ) : (
            <div className="note">Tidak ada cash yang perlu disetor untuk rute ini — rute hanya ditandai selesai.</div>
          )}
        </>
      ),
      foot: (m) => (
        <>
          <button type="button" className="btn" data-m="close" onClick={m.close}>
            Batal
          </button>
          <button className="btn btn-primary" type="submit">
            Tutup rute
          </button>
        </>
      ),
      submit: async (f, m) => {
        const body: Record<string, unknown> = {};
        const rec = f.elements.namedItem('received') as HTMLInputElement | null;
        if (rec) {
          body.received = Number(rec.value);
          body.note = (f.elements.namedItem('note') as HTMLInputElement).value;
        }
        const res = await api<{ deposit?: { no: string; diff: number } | null }>('POST', `/api/routes/${r.id}/close`, body);
        m.close();
        toast(
          res.deposit
            ? `Rute ditutup · setoran ${res.deposit.no}${res.deposit.diff ? ` (selisih ${rp(res.deposit.diff)})` : ''}`
            : 'Rute ditutup.',
        );
        goRute();
        void refreshCounts();
      },
    });
  }

  return { newRouteModal, openRoute, addToRoute, closeRouteModal };
}
